import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

/**
 * Landing side of `platform-admin`'s "View as tenant" impersonation handoff (§9.1) —
 * `backend/app/services/sso_service.py::initiate_impersonation_handoff`'s module
 * docstring has the full design rationale, `SsoConsumePage.tsx` is the sibling flow
 * this mirrors. Reached only via `platform-admin`'s `TenantDetailPage` opening
 * `?code=...` in a new tab right after `POST /platform/tenants/{id}/impersonate`
 * mints both the impersonation token and this handoff code. Exchanges the code for
 * the exact impersonation session (no refresh token — §9.1's non-renewable design)
 * and drops straight into the app, no password prompt — the operator already proved
 * who they are in platform-admin's own MFA-gated login.
 */
export function ImpersonationConsumePage() {
  const { status, loginWithImpersonationCode } = useAuth();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (!code || attempted.current) return;
    attempted.current = true;
    loginWithImpersonationCode(code).catch(() => {
      setError("This impersonation link has expired or was already used. Ask the platform operator to start a new session.");
    });
  }, [code, loginWithImpersonationCode]);

  if (status === "authenticated") return <Navigate to="/" replace />;
  if (!code) return <Navigate to="/login" replace />;

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>ZEL-MART</h1>
        {error ? (
          <>
            <p className="banner banner--error" role="alert">
              {error}
            </p>
            <p>
              <a href="/login">Go to login</a>
            </p>
          </>
        ) : (
          <p>Signing you in as the impersonated tenant user…</p>
        )}
      </div>
    </div>
  );
}
